// components/ListeAnnonces.js — Premium with Framer Motion
import React from 'react';
import { motion } from 'framer-motion';
import './ListeAnnonces.css';
import { useCurrency } from '../utils/currency';
import { useComparateur } from './common/Comparateur';
import { useI18n } from '../i18n/I18nContext';
import './common/Comparateur.css';

// ─── Animation variants ──────────────────────────────────────────────────────

const gridVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden:  { opacity: 0, y: 40, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const headerVariants = {
  hidden:  { opacity: 0, y: -16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

// ─── Component ───────────────────────────────────────────────────────────────

function ListeAnnonces({ annonces, onVoirDetail, favoris = [], onToggleFavori }) {
  const { formatPrix } = useCurrency();
  const { toggle: toggleComparer, isSelected: isCompared } = useComparateur();
  const { t } = useI18n();

  const isRecent = (dateCreation) => {
    if (!dateCreation) return false;
    const diff = Date.now() - new Date(dateCreation).getTime();
    return diff < 7 * 24 * 60 * 60 * 1000;
  };

  const getPrixParM2 = (prix, surface) => {
    if (!surface || surface <= 0) return null;
    return Math.round(prix / surface).toLocaleString('fr-DZ');
  };

  // Map equipment keys to SVG icons (no emoji)
  const EqIcons = {
    parking:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M9 15V9h4a2 2 0 010 4H9"/></svg>,
    balcon:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><rect x="9" y="14" width="6" height="7"/></svg>,
    ascenseur: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><polyline points="8 8 12 4 16 8"/><polyline points="16 16 12 20 8 16"/></svg>,
    meuble:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M2 14h20"/><path d="M12 7V3"/></svg>,
    gardien:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  };

  const etatMap = {
    neuf:     { label: 'Neuf',    color: '#059669' },
    bon_etat: { label: 'Bon état', color: '#0284c7' },
    a_renover:{ label: 'À rénover',color: '#d97706' },
  };

  return (
    <div>
      {/* Results header */}
      <motion.div
        className="results-header"
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <h2>{t.search.results(annonces.length)}</h2>
        <span className="results-badge">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          {t.search.verified}
        </span>
      </motion.div>

      {/* Cards grid */}
      <motion.div
        className="annonces-grid"
        variants={gridVariants}
        initial="hidden"
        animate="visible"
      >
        {annonces.map((annonce) => (
          <motion.div
            key={annonce.id}
            className="annonce-card"
            variants={cardVariants}
            whileHover={{
              y: -8,
              boxShadow: '0 20px 48px rgba(139,69,19,0.14), 0 6px 16px rgba(0,0,0,0.08)',
              transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onVoirDetail(annonce.id)}
            style={{ opacity: 1, transform: 'none' }} // override old CSS
          >
            {/* IMAGE */}
            <div className="annonce-image">
              <img
                src={
                  annonce.photos?.[0] ||
                  annonce.photo ||
                  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400'
                }
                alt={annonce.titre}
                loading="lazy"
              />

              {/* Photo count */}
              {annonce.photos?.length > 1 && (
                <div className="annonce-photo-count">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  {annonce.photos.length}
                </div>
              )}

              {/* Type badge */}
              <div className="annonce-badge">{annonce.type}</div>

              {/* New badge */}
              {isRecent(annonce.dateCreation || annonce.created_at) && (
                <div className="annonce-new-badge">{t.annonce.new}</div>
              )}

              {/* Verified */}
              <div className="annonce-verified-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                {t.annonce.verified}
              </div>

              {/* Favorite */}
              {onToggleFavori && (
                <motion.button
                  className={`annonce-favori-btn ${favoris.includes(annonce.id) ? 'is-favori' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onToggleFavori(annonce.id); }}
                  title={favoris.includes(annonce.id) ? t.annonce.removeFav : t.annonce.addFav}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.90 }}
                >
                  <svg
                    width="17" height="17"
                    viewBox="0 0 24 24"
                    fill={favoris.includes(annonce.id) ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                </motion.button>
              )}
            </div>

            {/* CONTENT */}
            <div className="annonce-content">
              <h3>{annonce.titre}</h3>

              {/* Price */}
              <div className="annonce-price">
                {formatPrix(annonce.prix)}
                {getPrixParM2(annonce.prix, annonce.surface) && (
                  <span className="prix-par-m2">
                    {getPrixParM2(annonce.prix, annonce.surface)} DZD/m²
                  </span>
                )}
              </div>

              {/* Details chips */}
              <div className="annonce-details">
                <span className="detail-chip">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {annonce.quartier}
                </span>
                {annonce.surface > 0 && (
                  <span className="detail-chip">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                    </svg>
                    {annonce.surface} m²
                  </span>
                )}
                {annonce.chambres > 0 && (
                  <span className="detail-chip">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 7v11a2 2 0 002 2h14a2 2 0 002-2V7"/>
                      <path d="M21 7H3l2-4h14l2 4z"/>
                    </svg>
                    {annonce.chambres} {t.annonce.rooms}
                  </span>
                )}
                {annonce.etage != null && (
                  <span className="detail-chip">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2"/>
                      <path d="M9 12h6"/>
                    </svg>
                    {annonce.etage === 0 ? 'RDC' : `Ét. ${annonce.etage}`}
                  </span>
                )}
                {annonce.etat && etatMap[annonce.etat] && (
                  <span
                    className="detail-chip"
                    style={{
                      color: etatMap[annonce.etat].color,
                      borderColor: `${etatMap[annonce.etat].color}25`,
                      background: `${etatMap[annonce.etat].color}0D`,
                    }}
                  >
                    {etatMap[annonce.etat].label}
                  </span>
                )}
              </div>

              {/* Equipment icons */}
              {(annonce.parking || annonce.balcon || annonce.ascenseur || annonce.meuble || annonce.gardien) && (
                <div className="annonce-equipements">
                  {annonce.parking   && <span title="Parking">{EqIcons.parking}</span>}
                  {annonce.balcon    && <span title="Balcon">{EqIcons.balcon}</span>}
                  {annonce.ascenseur && <span title="Ascenseur">{EqIcons.ascenseur}</span>}
                  {annonce.meuble    && <span title="Meublé">{EqIcons.meuble}</span>}
                  {annonce.gardien   && <span title="Gardien">{EqIcons.gardien}</span>}
                </div>
              )}

              {/* Description excerpt */}
              <p className="annonce-description">
                {annonce.description?.substring(0, 95)}
                {(annonce.description?.length || 0) > 95 ? '…' : ''}
              </p>

              {/* Actions */}
              <div className="annonce-card-actions">
                <button
                  className="btn btn-card"
                  onClick={(e) => { e.stopPropagation(); onVoirDetail(annonce.id); }}
                >
                  {t.annonce.seeDetails}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
                <button
                  className={`btn-comparer ${isCompared(annonce.id) ? 'selected' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleComparer(annonce); }}
                  title={t.annonce.compare}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6"  y1="20" x2="6"  y2="14"/>
                  </svg>
                  {isCompared(annonce.id) ? t.annonce.selected : t.annonce.compare}
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

export default ListeAnnonces;
